// @ts-nocheck
import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Hardcoded sendback prompts config
export const SENDBACK_PROMPTS: Record<string, { emoji: string; message: string }> = {
  teammate: { emoji: '🤝', message: 'Send a 🤝 to someone who helped you this week' },
  trophy: { emoji: '🏆', message: 'Know an elite play? Give someone a 🏆' },
  grind: { emoji: '💪', message: 'Who else is grinding? Show them some 💪' },
  flowers: { emoji: '🌸', message: 'Pass the respect forward with a 🌸' },
  fire: { emoji: '🔥', message: 'Hype someone else up with a 🔥' },
  brain: { emoji: '🧠', message: 'Know a smart player? Give them a 🧠' },
  clutch: { emoji: '🎯', message: 'Seen a clutch moment? Award it with a 🎯' },
  lockdown: { emoji: '🧱', message: 'Know a great defender? Give them a 🧱' },
  levelup: { emoji: '🚀', message: 'See someone improving? Give them a 🚀' },
};

interface DismissedPrompt {
  prompt_type: string;
  prompt_date: string;
}

export const useSendbackPrompts = (profileId?: string) => {
  const [dismissedToday, setDismissedToday] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);

  // Fetch today's dismissed prompts
  const fetchDismissed = useCallback(async () => {
    if (!profileId) return;
    
    setIsLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from('wall_sendback_completions')
        .select('prompt_type, prompt_date')
        .eq('profile_id', profileId)
        .eq('prompt_date', today);
      
      if (error) throw error;
      
      const dismissed = new Set((data || []).map((d: DismissedPrompt) => d.prompt_type));
      setDismissedToday(dismissed);
    } catch (err) {
      console.error('Error fetching dismissed prompts:', err);
    } finally {
      setIsLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    fetchDismissed();
  }, [fetchDismissed]);

  // Check if a prompt should be shown for a reaction type
  const shouldShowPrompt = useCallback((reactionType: string): boolean => {
    return !!SENDBACK_PROMPTS[reactionType] && !dismissedToday.has(reactionType);
  }, [dismissedToday]);

  // Get the prompt for a reaction type
  const getPrompt = useCallback((reactionType: string) => {
    return SENDBACK_PROMPTS[reactionType] || null;
  }, []);

  // Dismiss a prompt (mark as seen for today)
  const dismissPrompt = useCallback(async (promptType: string) => {
    if (!profileId) return;
    
    try {
      const today = new Date().toISOString().split('T')[0];
      
      await supabase
        .from('wall_sendback_completions')
        .upsert({
          profile_id: profileId,
          prompt_type: promptType,
          prompt_date: today,
          dismissed_at: new Date().toISOString(),
        }, {
          onConflict: 'profile_id,prompt_type,prompt_date'
        });
      
      // Update local state
      setDismissedToday(prev => new Set([...prev, promptType]));
    } catch (err) {
      console.error('Error dismissing prompt:', err);
    }
  }, [profileId]);

  // Mark a prompt as completed (user acted on it)
  const completePrompt = useCallback(async (promptType: string) => {
    if (!profileId) return;
    
    try {
      const today = new Date().toISOString().split('T')[0];
      
      await supabase
        .from('wall_sendback_completions')
        .upsert({
          profile_id: profileId,
          prompt_type: promptType,
          prompt_date: today,
          completed_at: new Date().toISOString(),
        }, {
          onConflict: 'profile_id,prompt_type,prompt_date'
        });
      
      // Update local state
      setDismissedToday(prev => new Set([...prev, promptType]));
    } catch (err) {
      console.error('Error completing prompt:', err);
    }
  }, [profileId]);

  return {
    shouldShowPrompt,
    getPrompt,
    dismissPrompt,
    completePrompt,
    isLoading,
  };
};
